import type { CandidateProfile, CandidateProfileSectionEntry } from '../api/candidate';

export type CandidateTeamSnapshot = {
  isTeamCandidate: boolean;
  teamSize: number;
  teamMemberNames: string[];
};

const parseBoolean = (value: unknown) => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') return value.toLowerCase() === 'true';
  return false;
};

const parseTeamMemberNames = (value: unknown) =>
  `${value ?? ''}`
    .split(',')
    .map((name) => name.trim())
    .filter(Boolean);

const dedupeStrings = (values: string[]) => Array.from(new Set(values.map((value) => value.trim()).filter(Boolean)));

function parseTeamMembersFromWorkExperience(entries: CandidateProfileSectionEntry[]) {
  return entries
    .filter((entry) => parseBoolean(entry.teamMember))
    .map((entry) => entry.name ?? entry.fullName ?? entry.title ?? '')
    .map((value) => value.trim())
    .filter(Boolean);
}

function parseTeamSize(value: unknown) {
  const parsed = Number.parseInt(`${value ?? ''}`, 10);
  if (!Number.isFinite(parsed) || parsed < 1) return 0;
  return parsed;
}

export function deriveCandidateTeamSnapshot(profile: CandidateProfile | undefined): CandidateTeamSnapshot {
  const personalInfo = profile?.['personal-info']?.[0] ?? {};
  const workExperience = profile?.['work-experience'] ?? [];

  const registerAsTeam = parseBoolean(personalInfo.registerAsTeam);
  const namesFromPersonalInfo = parseTeamMemberNames(personalInfo.teamMemberNames);
  const namesFromWorkExperience = parseTeamMembersFromWorkExperience(workExperience);
  const teamMemberNames = dedupeStrings([...namesFromPersonalInfo, ...namesFromWorkExperience]);
  const teamSizeFromProfile = parseTeamSize(personalInfo.teamSize);
  const teamSize = teamSizeFromProfile || teamMemberNames.length;
  const hasTeamSignals = teamSize >= 2 || teamMemberNames.length >= 2;

  return {
    isTeamCandidate: registerAsTeam || hasTeamSignals,
    teamSize,
    teamMemberNames,
  };
}
